import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { getImage, withArtDirection } from "gatsby-plugin-image";
import UnstyledImage from "./image";
import { useWindowSize } from "../utils/use-window-size.js";
import HeroCarousel from "./hero-carousel";
import { checkDarkMode } from "../utils/helpers.js";

const HeroWrapper = styled.div`
  width: 100%;
  position: relative;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};

  h1 {
    margin-bottom: 10px;
    text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.16);
    font-family: ${({ theme }) => theme.fontHeadings.fontFamily};
  }
  p {
    margin: 0 0 1rem 0;
    text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.26);
    font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  }
`;

const BigHero = styled(HeroWrapper)`
  height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 430px;
  }
`;
const FrontpageHero = styled(HeroWrapper)`
  height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 90vh;
  }
`;
const SmHero = styled(HeroWrapper)`
  height: 368px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 195px;
  }
`;
const HeroImage = styled(UnstyledImage)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: absolute;
  z-index: 0;
`;

const HeroVideo = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: absolute;
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 100px 0px 0 70px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 20px 40px;
    justify-content: center;
    height: 70%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 100%;
    padding: 20px;
  }
  a {
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      width: 100%;
      margin: 5px 0;
      max-width: 500px;
    }
    h1 {
      font-weight: 300;
      font-size: 3.3rem;
      margin-bottom: 10px;
      text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.16);
      @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
        font-size: 2.2rem;
      }
    }
    h2 {
      text-transform: uppercase;
      font-size: 3.3rem;
      font-weight: 600;
      margin: 0 0 1rem 0;
      text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.26);
      @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
        font-size: 2.2rem;
      }
    }
  }
`;

const FrontPageHeroContent = styled(HeroContent)`
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 80vh;
  }
`;
const SideBySideContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  color: ${({ theme }) => checkDarkMode("#000", theme)};
  background-color: ${({ theme }) => checkDarkMode("#ededed", theme)};
  justify-content: center;
  padding-left: 3rem;
  h1 {
    font-size: 40px;
  }
  p {
    font-size: 20px;
    text-shadow: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1.5rem;
    h1 {
      font-size: 30px;
    }
    p {
      font-size: 18px;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const SideBySideHeroVideo = styled.video`
  height: 100%;
  width: 50%;
  object-fit: cover;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const SideBySideHeroImage = styled(UnstyledImage)`
  height: 100%;
  width: 50%;
  object-fit: cover;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const SideBySideHero = styled.div`
  display: flex;
  width: 100%;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  gap: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    flex-direction: column;
  }
`;
const SideBySideCarouselWrapper = styled.div`
  width: 50%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const CarouselChildren = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-60%);
  min-width: 100%;
  max-height: 100%;
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  justify-content: center;
`;
const CarouselChildrenInnerWrapper = styled.div`
  max-width: 75%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 65%;
  }
`;
export default function Hero({
  mediaUrl,
  mediaAlt,
  media,
  mobileMedia,
  variant,
  children,
  mediaItems,
  mediaItemsMobile,
  backgroundImage,
}) {
  const multipleMedia = media?.mimeType && mobileMedia?.mimeType;

  // When both the desktop and the mobile hero are optimised images, let the browser
  // pick between them with <picture>. That puts the hero in the server-rendered HTML
  // so it can start loading immediately, instead of staying empty until JS has run
  // and measured the window.
  const artDirectedImage = React.useMemo(() => {
    const isImage = (item) => (item?.mimeType || "").startsWith("image/");
    const desktop = isImage(media) ? getImage(media?.localFile) : undefined;
    const mobile = isImage(mobileMedia)
      ? getImage(mobileMedia?.localFile)
      : undefined;
    if (!desktop || !mobile || desktop.layout !== mobile.layout) {
      return undefined;
    }
    return withArtDirection(desktop, [
      { media: "(max-width: 600px)", image: mobile },
    ]);
  }, [media, mobileMedia]);

  const [heroMedia, setMedia] = React.useState(
    multipleMedia && !artDirectedImage ? null : media
  );
  const windowSize = useWindowSize();
  const mediaSrc = (item) =>
    item?.localFile?.publicURL || item?.mediaItemUrl || item?.sourceUrl || "";
  const backgroundImageSrc = mediaSrc(backgroundImage);
  const heroMediaSrc = mediaSrc(heroMedia);
  const posterSrc =
    backgroundImageSrc && backgroundImageSrc.startsWith("http")
      ? backgroundImageSrc
      : backgroundImageSrc
        ? `${process.env.GATSBY_SITE_URL}${backgroundImageSrc}`
        : "";

  React.useEffect(() => {
    if (windowSize.width && multipleMedia && !artDirectedImage) {
      if (windowSize.width <= 600) {
        setMedia(mobileMedia);
      } else {
        setMedia(media);
      }
    }
  }, [media, mobileMedia, multipleMedia, artDirectedImage, windowSize]);

  if (variant === "big") {
    return (
      <BigHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <>
            <HeroVideo
              autoPlay={media !== null}
              loop
              muted
              playsInline
              preload={backgroundImageSrc ? "none" : "auto"}
              poster={posterSrc}
            >
              {heroMediaSrc && (
                <source src={heroMediaSrc} type={heroMedia.mimeType} />
              )}
            </HeroVideo>
            <HeroContent>{children}</HeroContent>
          </>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
              variant={variant}
            ></HeroCarousel>
            <CarouselChildren>
              <CarouselChildrenInnerWrapper>
                {children}
              </CarouselChildrenInnerWrapper>
            </CarouselChildren>
          </>
        ) : (
          <>
            <HeroImage
              src={mediaUrl}
              alt={mediaAlt}
              image={heroMedia}
              imageData={artDirectedImage}
              loading="eager"
              fetchpriority="high"
            />
            <HeroContent>{children}</HeroContent>
          </>
        )}
      </BigHero>
    );
  } else if (variant === "frontpage") {
    return (
      <FrontpageHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <>
            <HeroVideo
              autoPlay
              loop
              muted
              playsInline
              preload={backgroundImageSrc ? "none" : "auto"}
              poster={posterSrc}
            >
              {heroMediaSrc && (
                <source src={heroMediaSrc} type={heroMedia.mimeType} />
              )}
            </HeroVideo>
            <FrontPageHeroContent>{children}</FrontPageHeroContent>
          </>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
              variant={variant}
            ></HeroCarousel>
            <CarouselChildren>
              <CarouselChildrenInnerWrapper>
                {children}
              </CarouselChildrenInnerWrapper>
            </CarouselChildren>
          </>
        ) : (
          <>
            <HeroImage
              src={mediaUrl}
              alt={mediaAlt}
              image={heroMedia}
              imageData={artDirectedImage}
              loading="eager"
              fetchpriority="high"
            />
            <FrontPageHeroContent>{children}</FrontPageHeroContent>
          </>
        )}
      </FrontpageHero>
    );
  } else if (variant === "small") {
    return (
      <SmHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <HeroVideo
            autoPlay
            loop
            muted
            playsInline
            preload={backgroundImageSrc ? "none" : "auto"}
            poster={posterSrc}
          >
            {heroMediaSrc && (
              <source src={heroMediaSrc} type={heroMedia.mimeType} />
            )}
          </HeroVideo>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
              variant={variant}
            ></HeroCarousel>
            <CarouselChildren>
              <CarouselChildrenInnerWrapper>
                {children}
              </CarouselChildrenInnerWrapper>
            </CarouselChildren>
          </>
        ) : (
          <HeroImage
            src={mediaUrl}
            alt={mediaAlt}
            image={heroMedia}
            imageData={artDirectedImage}
            loading="eager"
            fetchpriority="high"
          />
        )}
      </SmHero>
    );
  } else if (variant === "side-by-side") {
    return (
      <SideBySideHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <SideBySideHeroVideo
            autoPlay
            loop
            muted
            playsInline
            preload={backgroundImageSrc ? "none" : "auto"}
            poster={posterSrc}
          >
            {heroMediaSrc && (
              <source src={heroMediaSrc} type={heroMedia.mimeType} />
            )}
          </SideBySideHeroVideo>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <SideBySideCarouselWrapper>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
            />
          </SideBySideCarouselWrapper>
        ) : (
          <SideBySideHeroImage
            src={mediaUrl}
            alt={mediaAlt}
            image={heroMedia}
            imageData={artDirectedImage}
            loading="eager"
            fetchpriority="high"
          />
        )}
        <SideBySideContent>{children}</SideBySideContent>
      </SideBySideHero>
    );
  }
}

Hero.propTypes = {
  mediaUrl: PropTypes.object,
  mediaAlt: PropTypes.string,
  media: PropTypes.object,
  mobileMedia: PropTypes.object,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["big", "frontpage", "small", "side-by-side"]),
  mediaItems: PropTypes.arrayOf(PropTypes.object),
  mediaItemsMobile: PropTypes.arrayOf(PropTypes.object),
  backgroundImage: PropTypes.object,
};

Hero.defaultProps = {
  children: undefined,
  mediaUrl: undefined,
  mediaAlt: undefined,
  media: undefined,
  mobileMedia: undefined,
  variant: "big",
  mediaItems: undefined,
  mediaItemsMobile: undefined,
  backgroundImage: undefined,
};
